import * as React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DrawIcon from '@mui/icons-material/Draw';
import CategoryIcon from '@mui/icons-material/Category';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import ArticleIcon from '@mui/icons-material/Article';
import GroupIcon from '@mui/icons-material/Group';
import ListIcon from '@mui/icons-material/List';
import GavelIcon from '@mui/icons-material/Gavel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon from '@mui/icons-material/Description';
import ConstructionIcon from '@mui/icons-material/Construction';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupsIcon from '@mui/icons-material/Groups';
import MedicationIcon from '@mui/icons-material/Medication';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  List,
  styled,
  alpha
} from "@mui/material";
import { LinkProps } from "react-router-dom";
import { Link, useLocation } from 'react-router-dom';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import LinkIcon from '@mui/icons-material/Link';
import { IApplicationData } from '../../model/application-data';
import { IAuthenticationData } from '../../model/authentication-data';


interface StyledListItemButtonProps extends LinkProps {
  active?: boolean;
  component?: React.ElementType;
}

const StyledListItemButton = styled(ListItemButton)<StyledListItemButtonProps>(({ theme, active }) => ({
  borderRadius: 8,
  margin: '4px 8px',
  minHeight: 48,
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(4px)',
  },

  ...(active && {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,

    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    }
  }),

  '& .MuiListItemIcon-root': {
    minWidth: 40,
    transition: 'color 0.2s ease-in-out',
  },

  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 400,
    transition: 'font-weight 0.2s ease-in-out',
  }
}));

const MenuListItem = ({ to, icon, text }: { to: string; icon: React.ReactNode; text: string }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <StyledListItemButton
      component={Link as React.ElementType}
      to={to}
      active={isActive}
    >
      <ListItemIcon>
        {icon}
      </ListItemIcon>
      <ListItemText primary={text} />
    </StyledListItemButton>
  );
};

interface MainListItemsProps {
  applicationData: IApplicationData;
  authenticationData?: IAuthenticationData;
}

export const MainListItems: React.FC<MainListItemsProps> = ({ applicationData, authenticationData }) => {
  return (
    <List sx={{ padding: '8px 0' }}>
      {applicationData.moduleId === 1 && (
        <>
          <MenuListItem to="/dashboard" icon={<DashboardIcon />} text="Dashboard" />
          <MenuListItem to="/envio-remessa" icon={<FileUploadIcon />} text="Envio de Remessas" />
          <MenuListItem to="/consulta-remessa" icon={<FindInPageIcon />} text="Consulta Remessa" />
        </>
      )}
      {applicationData.moduleId === 2 && (
        <>
          <MenuListItem to="/dashboard" icon={<DashboardIcon />} text="Dashboard" />
          {authenticationData?.type === 2 && (
            <>
              <MenuListItem to="/clientes" icon={<AccountBalanceIcon />} text="Clientes" />
              <MenuListItem to="/usuarios" icon={<GroupIcon />} text="Usuários" />
            </>
          )}
          <MenuListItem to="/consulta-remessa" icon={<FindInPageIcon />} text="Consulta Remessa" />
          <MenuListItem to="/diagramar-diario-oficial" icon={<DrawIcon />} text="Diagramar Diário Oficial" />
          <MenuListItem to="/consulta-diario-oficial" icon={<ImageSearchIcon />} text="Consulta Diário Oficial" />
        </>
      )}
      {applicationData.moduleId === 3 && (
        <>
          <MenuListItem to="/banners" icon={<BurstModeIcon />} text="Banners" />
          <MenuListItem to="/categoria-noticias" icon={<CategoryIcon />} text="Categoria de Notícias" />
          <MenuListItem to="/noticias" icon={<NewspaperIcon />} text="Notícias" />
          <MenuListItem to="/link-servicos" icon={<LinkIcon />} text="Links de Serviços" />
          <MenuListItem to="/estrutura-governamental" icon={<AccountBalanceIcon />} text="Estrutura Governamental" />
          <MenuListItem to="/paginas-customizadas" icon={<ArticleIcon />} text="Páginas Customizadas" />
          <MenuListItem to="/menus" icon={<ListIcon />} text="Menus" />
          <MenuListItem to="/orgaos" icon={<BusinessIcon />} text="Órgãos" />
          <MenuListItem to="/fornecedores" icon={<StoreIcon />} text="Fornecedores" />
          <MenuListItem to="/servidores" icon={<BadgeIcon />} text="Servidores" />
          <MenuListItem to="/licitacoes" icon={<GavelIcon />} text="Licitações" />
          <MenuListItem to="/convenios" icon={<HandshakeIcon />} text="Convênios" />
          <MenuListItem to="/emendas-parlamentares" icon={<AccountBalanceWalletIcon />} text="Emendas Parlamentares" />
          <MenuListItem to="/contratos" icon={<DescriptionIcon />} text="Contratos" />
          <MenuListItem to="/obras" icon={<ConstructionIcon />} text="Obras" />
          <MenuListItem to="/prestacao-contas" icon={<ReceiptLongIcon />} text="Prestação de Contas" />
          <MenuListItem to="/documentos-setoriais" icon={<LocalHospitalIcon />} text="Documentos Setoriais" />
          <MenuListItem to="/repasses-setoriais" icon={<PaymentsIcon />} text="Repasses Setoriais" />
          <MenuListItem to="/conselhos" icon={<GroupsIcon />} text="Conselhos" />
          <MenuListItem to="/estoques-medicamento" icon={<MedicationIcon />} text="Estoque de Medicamentos" />
          <MenuListItem to="/filas-espera" icon={<HourglassEmptyIcon />} text="Filas de Espera" />
        </>
      )}
    </List>
  );
};

export const secondaryListItems = (logout: () => void) => {
  return (
    <ListItemButton
      onClick={logout}
      sx={{
        borderRadius: 2,
        margin: '4px 8px',
        minHeight: 48,
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
          transform: 'translateX(4px)',
        },
        '& .MuiListItemIcon-root': {
          minWidth: 40,
          color: 'error.main'
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <ListItemIcon>
        <LogoutIcon />
      </ListItemIcon>
      <ListItemText primary="Sair" />
    </ListItemButton>
  );
};
